import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { FaShieldAlt, FaRobot, FaTrophy, FaUsers, FaChartLine, FaDollarSign, FaLayerGroup } from 'react-icons/fa';

const stats = [
  { value: "10,000+", label: "Active Traders", color: "#22c55e", icon: <FaUsers /> },
  { value: "95%", label: "Win Rate", color: "#3b82f6", icon: <FaChartLine /> },
  { value: "$2M+", label: "Profits Generated", color: "#22c55e", icon: <FaDollarSign /> },
  { value: "6+", label: "Platforms Supported", color: "#3b82f6", icon: <FaLayerGroup /> },
];

const features = [
  {
    icon: <FaShieldAlt className="text-2xl" />,
    color: "#22c55e",
    title: "Zero Martingale",
    desc: "Safe strategies only. No martingale, no hedging. Pure algorithmic precision with equity protection built-in.",
  },
  {
    icon: <FaRobot className="text-2xl" />,
    color: "#3b82f6",
    title: "Multi-Platform",
    desc: "Runs on MT4, MT5, TradeLocker, cTrader, DXTrade and MatchTrader with platform-optimized set files.",
  },
  {
    icon: <FaTrophy className="text-2xl" />,
    color: "#f59e0b",
    title: "Prop Firm Proven",
    desc: "Passes FTMO, Tiger Funded, E8 and all major challenges. Built for funded traders and live accounts alike.",
  },
];

export default function AboutUs() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });

  return (
    <section id="about" className="py-24 px-6 relative overflow-hidden" style={{ background: "var(--uea-dark)" }}>
      <div className="absolute inset-0 grid-bg opacity-50" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1/2 h-px"
        style={{ background: "linear-gradient(90deg, transparent, #22c55e, transparent)" }} />

      <div className="max-w-7xl mx-auto relative z-10" ref={ref}>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={isInView ? { opacity: 1, y: 0 } : {}} className="text-center mb-4">
          <span className="text-xs font-mono-code text-green-400 tracking-widest uppercase">// About UEA Trading Software</span>
        </motion.div>

        <motion.h2 initial={{ opacity: 0, y: 30 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ delay: 0.1 }}
          className="font-orbitron font-black text-3xl sm:text-4xl lg:text-5xl text-center text-white mb-6">
          Why Traders{" "}
          <span style={{ background: "linear-gradient(135deg, #22c55e, #3b82f6)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
            Choose UEA
          </span>
        </motion.h2>

        <motion.p initial={{ opacity: 0, y: 20 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ delay: 0.2 }}
          className="text-slate-400 text-center max-w-3xl mx-auto text-lg leading-relaxed mb-16">
          We are committed to delivering the most profitable trading bot services that work seamlessly on MetaTrader 4/5,
          TradeLocker, cTrader, DXTrade, MatchTrader and more.
        </motion.p>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {stats.map((s, i) => (
            <motion.div key={i} initial={{ opacity: 0, scale: 0.8 }} animate={isInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ delay: 0.3 + i * 0.1 }} whileHover={{ scale: 1.04 }}
              className="text-center p-6 rounded-2xl border"
              style={{ borderColor: `${s.color}25`, background: `${s.color}05` }}>
              <div className="flex justify-center mb-3" style={{ color: s.color }}>{s.icon}</div>
              <div className="font-orbitron text-3xl font-black mb-2" style={{ color: s.color }}>{s.value}</div>
              <div className="text-slate-400 text-sm uppercase tracking-wider">{s.label}</div>
            </motion.div>
          ))}
        </div>

        {/* Feature cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {features.map((f, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 30 }} animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.5 + i * 0.1 }}
              className="p-6 rounded-2xl border group hover:border-green-500/40 transition-all duration-300"
              style={{ borderColor: "rgba(59,130,246,0.1)", background: "var(--uea-card)" }}>
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4"
                style={{ background: `${f.color}15`, color: f.color, border: `1px solid ${f.color}30` }}>
                {f.icon}
              </div>
              <h3 className="font-orbitron text-white font-bold text-lg mb-3">{f.title}</h3>
              <p className="text-slate-400 text-sm leading-relaxed">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
